
import { Component, Vue, Prop, Watch, Emit } from 'vue-property-decorator';
import {
  Pagination,
  Row,
  Heading,
  SkeletonLoader
} from '@warrenbrasil/nebraska-web';
import { dateViewer } from '@/modules/common/helpers/date';
import {
  hasDetails,
  getStatus,
  getTransactionType
} from '../../helpers/transaction';
import { TransactionBadge } from '@/modules/common/components/TransactionBadge';
import { ITransactionGroup, ITransactions } from '@/types/models';
import {
  OverViewExtractDocument,
  OverViewExtractQuery,
  CashExtractDocument,
  CashExtractQuery,
  WealthTransactionsDocument,
  WealthTransactionsQuery,
  TradeTransactionsDocument,
  TradeTransactionsQuery
} from '@/modules/common/services/graphql/server/server.sdk';
import { formatCurrency } from '@/modules/common/helpers/currency';
import { ApolloQueryResult } from '@apollo/client';
import { groupBy } from 'lodash-es';
import { WarrenModules } from '@/types/navigation';
import { OpenTransactionEventPayload } from '../../types';

@Component({
  components: {
    Row,
    SkeletonLoader,
    TransactionBadge,
    Heading,
    Pagination
  }
})
export default class TransactionList extends Vue {
  private pageIndex = 1;
  private TRANSACTIONS_BY_PAGE = 8;
  private totalPages = 1;
  private transactions: ITransactionGroup[] = [];
  private isLoading = true;

  @Prop({ type: String, required: true, default: WarrenModules.Overview })
  private module?: WarrenModules;

  private mounted() {
    this.updateTransactions();
  }

  private onChangePage(page: number) {
    this.pageIndex = page;
    this.updateTransactions();
  }

  @Watch('module')
  private async onModuleUpdate(
    newModule: WarrenModules,
    oldModule: WarrenModules
  ) {
    if (newModule === oldModule) return;

    this.transactions = [];
    this.pageIndex = 1;
    this.updateTransactions();
  }

  @Emit()
  private openTransactionModal(
    transaction: ITransactions
  ): OpenTransactionEventPayload {
    return {
      id: transaction.id!,
      type: transaction.detailType!.value!
    };
  }

  private updateTransactions() {
    this.isLoading = true;

    switch (this.module) {
      case WarrenModules.Cash:
        this.getCashTransactions(this.pageIndex);
        break;
      case WarrenModules.Wealth:
        this.getWealthTransactions(this.pageIndex);
        break;
      case WarrenModules.Trade:
        this.getTradeTransactions(this.pageIndex);
        break;
      case WarrenModules.Overview:
        this.getOverViewTransactions(this.pageIndex);
        break;
      default:
        this.isLoading = false;
        break;
    }
  }

  private transactionHasDetails(transaction: ITransactions) {
    return (
      transaction.detailType &&
      hasDetails(transaction.detailType!.value!, transaction.status!.value!)
    );
  }

  private get showHelperView() {
    if (!this.isLoading && this.transactions!.length) {
      return !this.transactions![0].transactions.length;
    }

    return true;
  }

  private onClick(transaction: ITransactions) {
    if (this.transactionHasDetails(transaction)) {
      this.openTransactionModal(transaction);
    }
  }

  private async getOverViewTransactions(pageIndex: number) {
    try {
      const { data }: ApolloQueryResult<OverViewExtractQuery> =
        await this.$apollo.query({
          query: OverViewExtractDocument,
          variables: {
            size: this.TRANSACTIONS_BY_PAGE,
            index: pageIndex,
            dateFormat: 'DD/MM/YYYY'
          }
        });

      this.transactions = data!.globalSummary!.transactionsHistory!.list!;
      this.totalPages = data!.globalSummary!.transactionsHistory!.page!.amount!;
      this.isLoading = false;
    } catch (err) {
      this.isLoading = false;
    }
  }

  private async getCashTransactions(pageIndex: number) {
    try {
      const { data }: ApolloQueryResult<CashExtractQuery> =
        await this.$apollo.query({
          query: CashExtractDocument,
          variables: {
            size: this.TRANSACTIONS_BY_PAGE,
            index: pageIndex,
            dateFormat: 'DD/MM/YYYY'
          }
        });

      this.transactions = data!.cash!.extract!.list!;
      this.totalPages = data!.cash!.extract!.page!.amount!;
      this.isLoading = false;
    } catch (err) {
      this.isLoading = false;
    }
  }

  private async getWealthTransactions(pageIndex: number) {
    try {
      const { data }: ApolloQueryResult<WealthTransactionsQuery> =
        await this.$apollo.query({
          query: WealthTransactionsDocument,
          variables: {
            size: this.TRANSACTIONS_BY_PAGE,
            index: pageIndex
          }
        });

      const formattedTransactions =
        data!.wealth!.transactions!.transactions!.map(transaction => {
          return {
            ...transaction,
            status: {
              value: transaction.status,
              formatted: getStatus(transaction.status!)
            },
            type: {
              value: transaction.type,
              formatted: getTransactionType(transaction.type!)
            },
            detailType: {
              value: transaction.type
            },
            amount: {
              value: transaction.amount,
              formatted: `R$ ${formatCurrency(transaction.amount!)}`
            }
          };
        });

      const transactionsGroupByDate: any = groupBy(
        formattedTransactions,
        (transaction: any) => {
          if ('operatedAt' in transaction) {
            return dateViewer(
              transaction.operatedAt || new Date().toString(),
              'DD/MM/YYYY'
            );
          }
        }
      );

      const transactionDates = Object.keys(transactionsGroupByDate);
      const parsedTransactionGroup = transactionDates.map((index: string) => {
        return {
          date: index,
          balance: {
            value: null,
            formatted: null
          },
          transactions: transactionsGroupByDate[index]
        };
      });

      this.transactions = parsedTransactionGroup;
      this.totalPages = data!.wealth!.transactions!.page!.amount!;
      this.isLoading = false;
    } catch (err) {
      this.isLoading = false;
    }
  }

  private async getTradeTransactions(pageIndex: number) {
    try {
      const { data }: ApolloQueryResult<TradeTransactionsQuery> =
        await this.$apollo.query({
          query: TradeTransactionsDocument,
          variables: {
            size: this.TRANSACTIONS_BY_PAGE,
            index: pageIndex
          }
        });

      this.transactions = data!.trade!.transactions!.list.map(
        transactionGroup => {
          const parsedTransactions = transactionGroup.transactions.map(
            transaction => ({
              ...transaction,
              type: transaction.label!.type,
              description: transaction.label!.description
            })
          );

          return {
            ...transactionGroup,
            transactions: parsedTransactions,
            date: transactionGroup.date.formatted
          };
        }
      );
      this.totalPages = data!.trade!.transactions!.page!.amount!;
      this.isLoading = false;
    } catch (err) {
      this.isLoading = false;
    }
  }
}
